// Chakra Imports
import {
  Avatar,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  useColorMode,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Box,
  VStack,
  HStack,
  Divider,
  useBreakpointValue,
  Tooltip,
  Center,
} from '@chakra-ui/react';
// Custom Components
import { ItemContent } from '../../components/menu/ItemContent';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
// Assets
import { MdMenu, MdDashboard, MdSettings, MdLogout, MdPerson } from 'react-icons/md';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import logo from "../../assets/img/menom2_business.png";
import { restaurantService } from '../../services/restaurantService';
import routes from '../../routes';
import { getBusinessSlug } from '../../utils/slugify';
import { SidebarLinks } from '../../components/sidebar/components/Links';
import { SidebarBrand } from '../../components/sidebar/components/Brand';
import NavbarNotificationAdmin from './NavbarNotificationAdmin';
import api from '../../services/api';
import {
  cloudinaryDeliveryUrl,
  coerceCloudinaryAbsoluteIfPartial,
} from '../../utils/cloudinaryDeliveryUrl';

export default function HeaderLinks(props) {
const bgColor = "linear-gradient(135deg, #545454 30%, #E45858 100%)";
  const { secondary } = props;
  const { colorMode, toggleColorMode } = useColorMode();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [restaurantData, setRestaurantData] = useState({
    name: '',
    logo: ''
  });

  // Logo URL'ini oluştur - dosya adını encode et; Cloudinary tam URL ise dönüşüm ekle
  const getLogoUrl = (logoPath) => {
    if (!logoPath) return undefined;
    const partialCloud = coerceCloudinaryAbsoluteIfPartial(logoPath);
    if (partialCloud) {
      return cloudinaryDeliveryUrl(partialCloud, 'square500');
    }
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return cloudinaryDeliveryUrl(logoPath, 'square500');
    }
    let apiUrl = import.meta.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
    
    if (apiUrl && !apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
      apiUrl = (process.env.NODE_ENV === 'production' ? 'https://' : 'http://') + apiUrl;
    }
    
    const pathParts = logoPath.split('/');
    const fileName = pathParts.pop();
    const encodedPath = pathParts.join('/') + '/' + encodeURIComponent(fileName);
    return cloudinaryDeliveryUrl(`${apiUrl}${encodedPath}`, 'square500');
  };
  // Mobil menü için
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const businessData = JSON.parse(localStorage.getItem('business') || '{}');
  const businessName = businessData.businessName || 'İşletme';

  // Canlı saat için
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchRestaurantDetails();
  }, []);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await restaurantService.getRestaurantDetails();
      if (response.success) {
        setRestaurantData({
          name: response.data.name,
          logo: response.data.logo
        });
      }
    } catch (error) {
      console.error('Restaurant detayları alınırken hata:', error);
    }
  };

  // Tarih formatları
  const time = currentTime.toLocaleTimeString('tr-TR', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
  
  const day = currentTime.toLocaleDateString('tr-TR', { day: '2-digit' });
  const month = currentTime.toLocaleDateString('tr-TR', { month: 'short' });
  const weekDay = currentTime.toLocaleDateString('tr-TR', { weekday: 'long' });

  const handleLogout = async () => {
    // Backend'e logout isteği gönder (cookie'leri temizlemek için)
    try {
      await api.post('/api/business/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // localStorage'ı temizle
    localStorage.removeItem('token');
    localStorage.removeItem('business');
    localStorage.removeItem('restaurantId');
    localStorage.removeItem('userType');
    navigate('/auth/sign-in');
  };

  const handleProfileSettings = () => {
    const businessSlug = getBusinessSlug();
    if (businessSlug) {
      navigate(`/admin/${businessSlug}/profil-ayarlari`);
    } else {
      navigate('/admin/profil-ayarlari');
    }
  };

  const handleSuppliers = () => {
    const businessSlug = getBusinessSlug();
    if (businessSlug) {
      navigate(`/admin/${businessSlug}/tedarikciler`);
    } else {
      navigate('/admin/tedarikciler');
    }
  };

  const handleNavigation = (path) => {
    // Eğer path zaten /admin/ ile başlıyorsa ve business slug içermiyorsa ekle
    if (path.startsWith('/admin/') && !path.match(/^\/admin\/[^/]+\//)) {
      const businessSlug = getBusinessSlug();
      if (businessSlug) {
        // /admin/xxx formatındaki path'i /admin/businessSlug/xxx yap
        const pathWithoutAdmin = path.replace('/admin', '');
        navigate(`/admin/${businessSlug}${pathWithoutAdmin}`);
      } else {
        navigate(path);
      }
    } else {
      navigate(path);
    }
    onClose();
  };

  // Chakra Color Mode
  const navbarIcon = useColorModeValue('gray.400', 'white');
  const menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorBrand = useColorModeValue('brand.700', 'brand.400');
  const ethColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
  const ethBg = useColorModeValue('secondaryGray.300', 'navy.900');
  const ethBox = useColorModeValue('white', 'navy.800');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );
  const borderButton = useColorModeValue('secondaryGray.500', 'whiteAlpha.200');
  const iconColor = useColorModeValue('#364fab', '#364fab');
  const bgDrawer = useColorModeValue('white', 'navy.800');
  const bgBlur = useColorModeValue(
    'rgba(244, 247, 254, 0.8)',
    'rgba(11, 20, 55, 0.8)',
  );
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const hoverBgRed = useColorModeValue('red.50', 'whiteAlpha.100');
  const textColorSecondary = useColorModeValue('gray.500', 'gray.400');
  const unreadBg = useColorModeValue('#364fab08', '#364fab20');

  return (
    <Flex
      w={{ base: '100%', sm: 'auto' }}
      alignItems="center"
      flexDirection="row"
      bg={menuBg}
      flexWrap="nowrap"
      p={{ base: '6px', sm: '10px' }}
      borderRadius="30px"
      boxShadow={shadow}
      gap={{ base: 1, sm: 1.5 }}
      overflow="visible"
      justifyContent={{ base: 'flex-end', md: 'flex-start' }}
    >
      {/* Saat/Tarih - Responsive: 425px altında alt alta */}
      <Flex 
        align="center" 
        bg={useColorModeValue('white', 'navy.800')}
        borderRadius="16px"
        p={{ base: '4px 6px', sm: '8px 12px' }}
        boxShadow="sm"
        border="1px solid"
        borderColor={iconColor}
        mr={{ base: 1, sm: 3 }}
        display="flex"
        flexShrink={0}
        flexDirection={{ base: 'column', sm: 'row' }}
        gap={{ base: 0, sm: 2 }}
        minW={{ base: 'auto', sm: 'auto' }}
      >
        <Text
          fontSize={{ base: '10px', sm: 'lg' }}
          fontWeight="bold"
          color={textColor}
          mr={0}
          whiteSpace="nowrap"
          lineHeight={{ base: '1.1', sm: '1.5' }}
          textAlign={{ base: 'center', sm: 'left' }}
        >
          {time}
        </Text>
        <Box 
          h="20px" 
          w="1px" 
          bg={borderColor} 
          mr={3} 
          display={{ base: 'none', sm: 'block' }} 
          sx={{
            '@media (max-width: 425px)': {
              display: 'none',
            },
          }}
        />
        <Text
          fontSize={{ base: '9px', sm: 'sm' }}
          color="gray.500"
          fontWeight="medium"
          whiteSpace="nowrap"
          lineHeight={{ base: '1.1', sm: '1.5' }}
          textAlign={{ base: 'center', sm: 'left' }}
          sx={{
            '@media (max-width: 425px)': {
              display: 'none',
            },
          }}
        >
          {day} {month} {weekDay}
        </Text>
      </Flex>
      
      {/* Bildirimler - Her zaman görünür */}
      <Box flexShrink={0}>
        <NavbarNotificationAdmin
          shadow={shadow}
          menuBg={menuBg}
          textColor={textColor}
          hoverBg={hoverBg}
          iconColor={iconColor}
          textColorSecondary={textColorSecondary}
          unreadBg={unreadBg}
        />
      </Box>

      {/* Tema Değiştirme - 530px altında gizle */}
      <Button
        variant='no-hover'
        mr={{ base: 0, sm: 2 }}
        ml={{ base: 0, sm: 2 }}
        bg='transparent'
        p='0px'
        minW='unset'
        minH='unset'
        h='18px'
        w='max-content'
        onClick={toggleColorMode}
        display={{ base: 'none', sm: 'flex' }}
        flexShrink={0}
      >
        <Icon
          me='10px'
          h='18px'
          w='18px'
          color={iconColor}
          as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
        />
      </Button>

      {/* Avatar Menü - Her zaman görünür */}
      <Box flexShrink={0}>
        <Menu>
          <MenuButton p='0px'>
            <Avatar
              _hover={{ cursor: 'pointer' }}
              color="white"
              name={restaurantData.name}
              src={getLogoUrl(restaurantData.logo)}
              bg={restaurantData.logo ? "transparent" : "#11047A"}
              size={{ base: "sm", sm: "md" }}
              w={{ base: "36px", sm: "44px" }}
              h={{ base: "36px", sm: "44px" }}
              borderRadius="12px"
              border="2px solid"
              onError={(e) => {
                // Sessizce hata yok say (Avatar fallback kullanır)
              }}
            />
          </MenuButton>
          <MenuList
            boxShadow={shadow}
            p='0px'
            mt='10px'
            borderRadius='20px'
            bg={menuBg}
            border='none'>
            <Flex w='100%' mb='0px'>
              <Text
                ps='20px'
                pt='16px'
                pb='10px'
                w='100%'
                borderBottom='1px solid'
                borderColor={borderColor}
                fontSize='sm'
                fontWeight='700'
                color={iconColor}>
                👋&nbsp; Merhaba, {restaurantData.name || businessName}
              </Text>
            </Flex>
            <Flex flexDirection='column' p='10px'>
              <MenuItem
                _hover={{ bg: 'none' }}
                _focus={{ bg: 'none' }}
                borderRadius='8px'
                px='14px'
                onClick={handleProfileSettings}>
                <Text fontSize='sm'>Profil Ayarları</Text>
              </MenuItem>
              <MenuItem
                _hover={{ bg: 'none' }}
                _focus={{ bg: 'none' }}
                borderRadius='8px'
                px='14px'
                onClick={handleSuppliers}>
                <Text fontSize='sm'>Tedarikçiler</Text>
              </MenuItem>
              <MenuItem
                _hover={{ bg: 'none' }}
                _focus={{ bg: 'none' }}
                color='red.400'
                borderRadius='8px'
                px='14px'
                onClick={handleLogout}>
                <Text fontSize='sm'>Çıkış Yap</Text>
              </MenuItem>
            </Flex>
          </MenuList>
        </Menu>
      </Box>

      {/* Mobil Menü Butonu - 1200px (xl) altında görünür */}
      <IconButton
        aria-label="Menü"
        icon={<Icon as={MdMenu} />}
        variant="ghost"
        onClick={onOpen}
        ml={{ base: 0, sm: 1.5 }}
        color={navbarIcon}
        size={{ base: "sm", sm: "md" }}
        display={{ base: 'flex', xl: 'none' }}
        flexShrink={0}
      />

      {/* Mobil Menü Drawer - Sadece 1200px altında açılır */}
      <Drawer 
        isOpen={isOpen} 
        placement="left" 
        onClose={onClose} 
        size="xs"
      >
        <DrawerOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.4)" />
        <DrawerContent 
          bg={bgDrawer} 
          boxShadow={shadow}
          borderRightRadius="20px"
          maxW="280px"
        >
          <DrawerCloseButton 
            color={textColor} 
            size="lg" 
            mt={2} 
            mr={2}
          />
          <DrawerBody 
            p={4} 
            display="flex" 
            flexDirection="column" 
            justifyContent="center"
          >
            <Box mb={6} mt={8}>
              <Center mb={4}>
                <SidebarBrand />
              </Center>
            </Box>
            <Divider mb={4} />
            <SidebarLinks routes={routes} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};